import React from "react";
import { MallClubCard as MallClubCardType } from "@/src/types";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { User, ShieldCheck, Award, Star, Zap } from "lucide-react";
import { motion } from "motion/react";
import { cn } from "@/src/lib/utils";

interface MallClubCardProps {
  card: MallClubCardType;
  userName: string;
}

export function MallClubCard({ card, userName }: MallClubCardProps) {
  const getTierIcon = () => {
    switch (card.tier) {
      case "silver": return <Award className="h-6 w-6 text-slate-400" />;
      case "gold": return <Star className="h-6 w-6 text-yellow-500" />;
      case "platinum": return <Zap className="h-6 w-6 text-indigo-500" />;
      default: return <User className="h-6 w-6 text-primary" />;
    }
  };

  const getTierColors = () => {
    switch (card.tier) {
      case "silver": return "from-slate-100 to-slate-300 text-slate-800 border-slate-400";
      case "gold": return "from-yellow-100 to-yellow-400 text-yellow-900 border-yellow-500";
      case "platinum": return "from-indigo-100 to-indigo-500 text-indigo-900 border-indigo-600";
      default: return "from-primary/10 to-primary/30 text-primary-foreground border-primary/40";
    }
  };

  const tierName = {
    members: "Miembro Club",
    silver: "Club Plata",
    gold: "Club Oro",
    platinum: "Club Platino"
  }[card.tier];

  return (
    <motion.div
      initial={{ rotateY: -20, opacity: 0 }}
      animate={{ rotateY: 0, opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="perspective-1000"
    >
      <Card className={cn(
        "relative w-full max-w-md h-56 rounded-2xl overflow-hidden border-2 shadow-xl bg-gradient-to-br",
        getTierColors()
      )}>
        {/* Background Patterns */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute -right-8 -top-8 w-48 h-48 rounded-full border-[20px] border-current" />
          <div className="absolute -left-12 -bottom-12 w-64 h-64 rounded-full border-[10px] border-current" />
        </div>

        <div className="relative h-full p-6 flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/50 backdrop-blur-sm rounded-xl">
                <ShieldCheck className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h3 className="font-black text-xl tracking-tighter uppercase leading-none">Mall Virtual</h3>
                <p className="text-[10px] font-bold opacity-70">VALENCIA VIRTUAL</p>
              </div>
            </div>
            <Badge variant="outline" className="bg-white/20 backdrop-blur-sm border-white/40 uppercase font-bold text-[10px]">
              {tierName}
            </Badge>
          </div>

          <div className="space-y-4">
            <div className="space-y-0.5">
              <p className="text-[10px] font-bold uppercase opacity-60">Titular de la tarjeta</p>
              <p className="text-xl font-bold tracking-tight truncate">{userName}</p>
            </div>

            <div className="flex justify-between items-end">
              <div>
                <p className="text-[10px] font-bold uppercase opacity-60">ID Miembro</p>
                <p className="font-mono font-bold tracking-widest">{card.cardId || "EM-XXXXX"}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-bold uppercase opacity-60">Puntos Club</p>
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 fill-current" />
                  <span className="text-2xl font-black">{card.points || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Gloss Effect */}
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-white/20 pointer-events-none" />
      </Card>
    </motion.div>
  );
}
